import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { invalidateUserSessionsMutation } from "~/modules/auth/network";
import { lookupUserByIdQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/users/force-sign-out/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.userId) {
      throw new Error("User ID is required");
    }

    queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({ pathParams: { userId: params.userId } })
    );
  },
});

function RouteComponent() {
  return (
    <PermissionGuard permission="users:logout">
      <ForceSignOutPage />
    </PermissionGuard>
  );
}

function ForceSignOutPage() {
  const { userId } = Route.useParams();
  const [showConfirm, setShowConfirm] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const { data } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  const { mutate, isPending } = invalidateUserSessionsMutation.useMutation({
    onSuccess() {
      setShowConfirm(false);
      setSucceeded(true);
    },
    onError() {
      setShowConfirm(false);
      toast.error("Failed to invalidate sessions. Please try again.");
    },
  });

  const userLabel = data?.user?.name || data?.user?.email || userId;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/users/view/$userId"
          params={{ userId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to User
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-bold">Force Sign Out</h1>
      </div>

      <div className="max-w-lg space-y-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
          <p className="font-medium">Warning</p>
          <p className="mt-1">
            This will immediately log <span className="font-semibold">{userLabel}</span> out of all
            active sessions across all devices. The user will be required to sign in again.
          </p>
        </div>

        {succeeded ? (
          <p className="text-sm text-muted-foreground">
            All user auth sessions have been invalidated. All devices will be signed out within an
            hour.
          </p>
        ) : (
          <Button variant="destructive" onClick={() => setShowConfirm(true)}>
            Force Sign Out
          </Button>
        )}
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Force Sign Out</DialogTitle>
            <DialogDescription>
              Are you sure you want to sign out <span className="font-semibold">{userLabel}</span>{" "}
              from all devices? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => mutate({ params: { userId } })}
              disabled={isPending}
            >
              {isPending ? "Signing out..." : "Force Sign Out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
